import { Fragment, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BookOpen,
  ChevronDown,
  ChevronRight,
  Eye,
  ImageIcon,
  RefreshCw,
  Store as StoreIcon,
} from 'lucide-react';
import { Link } from 'wouter';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { apiFetch } from '@/lib/api';

interface Retailer {
  id: number;
  name: string;
}

interface BookingCycle {
  id: number;
  name: string;
  retailerId: number;
  startDate?: string;
  endDate?: string;
  start_date?: string;
  end_date?: string;
}

interface Operator {
  id: number;
  name: string;
}

interface ConfirmedPostingLine {
  id: string;
  sourceCycleId: number;
  rootTaskId: number;
  effectiveTaskId: number;
  originalTaskId: number | null;
  sourceAssignmentId: number | null;
  campaign: { id: number; name: string };
  store: { id: number; name: string };
  format: { id: number; name: string };
  confirmedQuantity: number;
  sourceTaskType: 'POSTING' | 'REPOST' | string;
  postingEventId: number;
  reviewDecisionId: number | null;
  approvedAt: string | null;
  operator: { id: number; name: string } | null;
  approver: { id: number; name: string } | null;
  image: {
    assetId: number | null;
    url: string | null;
    imageCount: number;
  };
}

interface ConfirmedPostingsResponse {
  sourceCycle: { id: number; name: string };
  summary: {
    lineCount: number;
    repostLines: number;
    linesWithoutImage: number;
    zeroQuantityLines: number;
    totalConfirmedQuantity: number;
  };
  lines: ConfirmedPostingLine[];
}

interface StoreGroup {
  key: string;
  storeId: number;
  storeName: string;
  operatorId: number | null;
  operatorName: string;
  lineCount: number;
  totalConfirmedQuantity: number;
  lines: ConfirmedPostingLine[];
}

interface OperatorGroup {
  key: string;
  operatorId: number | null;
  operatorName: string;
  storeCount: number;
  lineCount: number;
  totalConfirmedQuantity: number;
  stores: StoreGroup[];
}

interface CampaignRowGroup {
  key: string;
  campaignName: string;
  postingEventId: number;
  reviewDecisionId: number | null;
  sourceTaskType: string;
  approverName: string;
  approvedAt: string | null;
  photoUrl: string | null;
  totalConfirmedQuantity: number;
  rows: ConfirmedPostingLine[];
}

function getCycleStart(cycle: BookingCycle) {
  return cycle.startDate ?? cycle.start_date ?? null;
}

function getCycleEnd(cycle: BookingCycle) {
  return cycle.endDate ?? cycle.end_date ?? null;
}

function compareCyclesAscending(a: BookingCycle, b: BookingCycle) {
  const startA = getCycleStart(a);
  const startB = getCycleStart(b);
  if (startA && startB) {
    const timeA = new Date(startA).getTime();
    const timeB = new Date(startB).getTime();
    if (timeA !== timeB) {
      return timeA - timeB;
    }
  }
  return a.id - b.id;
}

function summaryCards(summary: ConfirmedPostingsResponse['summary']) {
  return [
    { key: 'lines', label: 'Approved lines', value: summary.lineCount },
    { key: 'quantity', label: 'Approved quantity', value: summary.totalConfirmedQuantity },
    { key: 'reposts', label: 'From reposts', value: summary.repostLines },
    { key: 'zero', label: 'Zero quantity', value: summary.zeroQuantityLines },
    { key: 'missing_image', label: 'No photo', value: summary.linesWithoutImage },
  ];
}

function groupStores(lines: ConfirmedPostingLine[]): StoreGroup[] {
  const groups = new Map<string, StoreGroup>();

  for (const line of lines) {
    const key = `${line.store.id}:${line.operator?.id ?? 'unassigned'}`;
    const existing = groups.get(key);

    if (!existing) {
      groups.set(key, {
        key,
        storeId: line.store.id,
        storeName: line.store.name,
        operatorId: line.operator?.id ?? null,
        operatorName: line.operator?.name ?? 'Unassigned',
        lineCount: 1,
        totalConfirmedQuantity: line.confirmedQuantity,
        lines: [line],
      });
      continue;
    }

    existing.lineCount += 1;
    existing.totalConfirmedQuantity += line.confirmedQuantity;
    existing.lines.push(line);
  }

  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      lines: [...group.lines].sort((a, b) => {
        if (a.campaign.name !== b.campaign.name) {
          return a.campaign.name.localeCompare(b.campaign.name);
        }
        if (a.format.name !== b.format.name) {
          return a.format.name.localeCompare(b.format.name);
        }
        return new Date(b.approvedAt ?? 0).getTime() - new Date(a.approvedAt ?? 0).getTime();
      }),
    }))
    .sort((a, b) => a.storeName.localeCompare(b.storeName));
}

function groupOperators(storeGroups: StoreGroup[]): OperatorGroup[] {
  const groups = new Map<string, OperatorGroup>();

  for (const storeGroup of storeGroups) {
    const key = `${storeGroup.operatorId ?? 'unassigned'}`;
    const existing = groups.get(key);

    if (!existing) {
      groups.set(key, {
        key,
        operatorId: storeGroup.operatorId,
        operatorName: storeGroup.operatorName,
        storeCount: 1,
        lineCount: storeGroup.lineCount,
        totalConfirmedQuantity: storeGroup.totalConfirmedQuantity,
        stores: [storeGroup],
      });
      continue;
    }

    existing.storeCount += 1;
    existing.lineCount += storeGroup.lineCount;
    existing.totalConfirmedQuantity += storeGroup.totalConfirmedQuantity;
    existing.stores.push(storeGroup);
  }

  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      stores: [...group.stores].sort((a, b) => a.storeName.localeCompare(b.storeName)),
    }))
    .sort((a, b) => a.operatorName.localeCompare(b.operatorName));
}

function groupCampaignRows(lines: ConfirmedPostingLine[]): CampaignRowGroup[] {
  const groups = new Map<string, CampaignRowGroup>();

  for (const line of lines) {
    const key = `${line.campaign.id}:${line.postingEventId}:${line.sourceTaskType}`;
    const existing = groups.get(key);

    if (!existing) {
      groups.set(key, {
        key,
        campaignName: line.campaign.name,
        postingEventId: line.postingEventId,
        reviewDecisionId: line.reviewDecisionId,
        sourceTaskType: line.sourceTaskType,
        approverName: line.approver?.name ?? 'Unknown',
        approvedAt: line.approvedAt,
        photoUrl: line.image.url,
        totalConfirmedQuantity: line.confirmedQuantity,
        rows: [line],
      });
      continue;
    }

    existing.totalConfirmedQuantity += line.confirmedQuantity;
    existing.rows.push(line);
  }

  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      rows: [...group.rows].sort((a, b) => a.format.name.localeCompare(b.format.name)),
    }))
    .sort((a, b) => a.campaignName.localeCompare(b.campaignName));
}

export default function AdminPostingsPage() {
  const [selectedRetailerId, setSelectedRetailerId] = useState<number | null>(null);
  const [selectedYear, setSelectedYear] = useState<string>(() => new Date().getFullYear().toString());
  const [sourceCycleId, setSourceCycleId] = useState<number | null>(null);
  const [selectedOperator, setSelectedOperator] = useState<string>('all');
  const [selectedStore, setSelectedStore] = useState<string>('all');
  const [repostOnly, setRepostOnly] = useState(false);
  const [missingImageOnly, setMissingImageOnly] = useState(false);
  const [expandedOperators, setExpandedOperators] = useState<Set<string>>(new Set());

  const yearOptions = useMemo(() => {
    const baseYear = new Date().getFullYear();
    return [baseYear - 1, baseYear, baseYear + 1];
  }, []);

  const { data: retailers = [] } = useQuery<Retailer[]>({
    queryKey: ['/api/retailers'],
    queryFn: async () => {
      const res = await apiFetch('/api/retailers');
      if (!res.ok) throw new Error('Failed to fetch retailers');
      const data = await res.json();
      return Array.isArray(data) ? data : (data.data || []);
    },
  });

  const { data: cycles = [] } = useQuery<BookingCycle[]>({
    queryKey: ['/api/booking-cycles', selectedRetailerId, selectedYear],
    enabled: !!selectedRetailerId,
    queryFn: async () => {
      const yearQuery = selectedYear ? `&year=${selectedYear}` : '';
      const res = await apiFetch(`/api/booking-cycles?retailerId=${selectedRetailerId}${yearQuery}`);
      if (!res.ok) throw new Error('Failed to fetch cycles');
      const data = await res.json();
      const rows = Array.isArray(data) ? data : (data.data || []);
      return rows
        .filter((cycle: any) => cycle.retailerId === selectedRetailerId || cycle.retailer_id === selectedRetailerId)
        .sort((a: BookingCycle, b: BookingCycle) => compareCyclesAscending(a, b));
    },
  });

  const filteredCycles = useMemo(() => {
    if (!selectedYear) return cycles;
    const selectedYearNumber = Number(selectedYear);
    return cycles.filter((cycle) => {
      const startYear = getCycleStart(cycle) ? new Date(getCycleStart(cycle) as string).getFullYear() : null;
      const endYear = getCycleEnd(cycle) ? new Date(getCycleEnd(cycle) as string).getFullYear() : null;
      const cycleYear = startYear ?? endYear;
      if (!cycleYear) return true;
      return cycleYear === selectedYearNumber;
    });
  }, [cycles, selectedYear]);

  const { data: operators = [] } = useQuery<Operator[]>({
    queryKey: ['/api/users/operators'],
    queryFn: async () => {
      const res = await apiFetch('/api/users/operators');
      if (!res.ok) throw new Error('Failed to fetch operators');
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    },
  });

  const postingsQuery = useQuery<ConfirmedPostingsResponse>({
    queryKey: ['/api/postings/confirmed', sourceCycleId, repostOnly, missingImageOnly, selectedOperator, selectedStore],
    enabled: !!sourceCycleId,
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('sourceCycleId', String(sourceCycleId));
      params.set('repostOnly', String(repostOnly));
      params.set('missingImageOnly', String(missingImageOnly));
      if (selectedOperator !== 'all') params.set('operatorId', selectedOperator);
      if (selectedStore !== 'all') params.set('storeId', selectedStore);
      const res = await apiFetch(`/api/postings/confirmed?${params.toString()}`);
      if (!res.ok) throw new Error('Failed to fetch confirmed postings');
      return res.json();
    },
  });

  const rows = postingsQuery.data?.lines ?? [];
  const storeGroups = useMemo(() => groupStores(rows), [rows]);
  const operatorGroups = useMemo(() => groupOperators(storeGroups), [storeGroups]);

  const storeOptions = useMemo(() => {
    const map = new Map<number, string>();
    for (const row of postingsQuery.data?.lines ?? []) {
      map.set(row.store.id, row.store.name);
    }
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [postingsQuery.data?.lines]);

  const headline = useMemo(() => {
    if (!postingsQuery.data?.sourceCycle) {
      return 'Choose a previous posting cycle to inspect the approved posting outcomes that now drive removals.';
    }
    return `Showing the current effective approved posting outcomes for ${postingsQuery.data.sourceCycle.name}.`;
  }, [postingsQuery.data?.sourceCycle]);

  const toggleOperator = (key: string) => {
    setExpandedOperators((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="p-6 space-y-6">
      <PageHeader title="Postings" description={headline} icon={BookOpen}>
        <Link href="/admin/removals-preview">
          <Button variant="outline">
            <Eye className="mr-2 h-4 w-4" />
            Open Removals
          </Button>
        </Link>
      </PageHeader>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-5">
          <div className="space-y-2">
            <div className="text-sm font-medium">Retailer</div>
            <Select
              value={selectedRetailerId ? String(selectedRetailerId) : undefined}
              onValueChange={(value) => {
                setSelectedRetailerId(Number(value));
                setSourceCycleId(null);
                setSelectedOperator('all');
                setSelectedStore('all');
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select retailer" />
              </SelectTrigger>
              <SelectContent>
                {retailers.map((retailer) => (
                  <SelectItem key={retailer.id} value={String(retailer.id)}>
                    {retailer.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <div className="text-sm font-medium">Year</div>
            <Select value={selectedYear} onValueChange={(value) => { setSelectedYear(value); setSourceCycleId(null); }}>
              <SelectTrigger>
                <SelectValue placeholder="Select year" />
              </SelectTrigger>
              <SelectContent>
                {yearOptions.map((year) => (
                  <SelectItem key={year} value={String(year)}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <div className="text-sm font-medium">Posting cycle</div>
            <Select
              value={sourceCycleId ? String(sourceCycleId) : undefined}
              onValueChange={(value) => {
                setSourceCycleId(Number(value));
                setSelectedStore('all');
              }}
              disabled={!selectedRetailerId}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select cycle" />
              </SelectTrigger>
              <SelectContent>
                {filteredCycles.map((cycle) => (
                  <SelectItem key={cycle.id} value={String(cycle.id)}>
                    {`Cycle ${cycle.name} (ID ${cycle.id})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <div className="text-sm font-medium">Operator</div>
            <Select value={selectedOperator} onValueChange={setSelectedOperator}>
              <SelectTrigger>
                <SelectValue placeholder="Select operator" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All operators</SelectItem>
                {operators.length > 0 ? operators.map((operator) => (
                  <SelectItem key={operator.id} value={String(operator.id)}>
                    {operator.name}
                  </SelectItem>
                )) : (
                  <SelectItem value="none" disabled>No operators available</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <div className="text-sm font-medium">Store</div>
            <Select value={selectedStore} onValueChange={setSelectedStore}>
              <SelectTrigger>
                <SelectValue placeholder="Select store" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All stores</SelectItem>
                {storeOptions.map((store) => (
                  <SelectItem key={store.id} value={String(store.id)}>
                    {store.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex flex-wrap items-center gap-6 pt-6">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={repostOnly} onCheckedChange={(checked) => setRepostOnly(checked === true)} />
            <span>Reposts only</span>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={missingImageOnly} onCheckedChange={(checked) => setMissingImageOnly(checked === true)} />
            <span>Missing photo only</span>
          </label>
        </CardContent>
      </Card>

      {postingsQuery.data && (
        <div className="grid gap-4 md:grid-cols-5">
          {summaryCards(postingsQuery.data.summary).map((item) => (
            <Card key={item.key}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">{item.label}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-semibold">{item.value.toLocaleString()}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {!sourceCycleId ? (
        <Card>
          <CardContent className="pt-6">
            <div className="rounded-md border border-dashed p-8 text-sm text-muted-foreground">
              Select a retailer and posting cycle to load confirmed posting outcomes.
            </div>
          </CardContent>
        </Card>
      ) : postingsQuery.isLoading ? (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <RefreshCw className="h-4 w-4 animate-spin" />
              Loading approved postings…
            </div>
          </CardContent>
        </Card>
      ) : postingsQuery.isError ? (
        <Card>
          <CardContent className="pt-6">
            <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
              {postingsQuery.error instanceof Error ? postingsQuery.error.message : 'Failed to load postings'}
            </div>
          </CardContent>
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="rounded-md border border-dashed p-8 text-sm text-muted-foreground">
              No confirmed posting rows matched this cycle and filter set.
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {operatorGroups.map((operatorGroup) => {
            const isExpanded = selectedOperator !== 'all' || expandedOperators.has(operatorGroup.key);

            return (
              <div key={operatorGroup.key} className="space-y-4">
                <Card className="border shadow-sm">
                  <CardContent className="flex items-center justify-between gap-4 py-4">
                    <button
                      type="button"
                      onClick={() => toggleOperator(operatorGroup.key)}
                      className="flex flex-1 items-center gap-3 text-left"
                    >
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      )}
                      <div className="text-xl font-semibold">{operatorGroup.operatorName}</div>
                      <Badge variant="secondary">{operatorGroup.storeCount} stores</Badge>
                      <Badge variant="outline">{operatorGroup.lineCount} approved lines</Badge>
                    </button>
                    <div className="text-right text-sm text-muted-foreground">
                      <div>Approved quantity</div>
                      <div className="text-2xl font-semibold text-foreground">{operatorGroup.totalConfirmedQuantity}</div>
                    </div>
                  </CardContent>
                </Card>

                {isExpanded && operatorGroup.stores.map((storeGroup) => {
                  const campaignGroups = groupCampaignRows(storeGroup.lines);

                  return (
                    <Card key={storeGroup.key} className="ml-4 border shadow-sm">
                      <CardHeader className="flex flex-row items-start justify-between gap-4">
                        <div>
                          <CardTitle className="flex items-center gap-2 text-2xl">
                            <StoreIcon className="h-5 w-5 text-muted-foreground" />
                            {storeGroup.storeName}
                            <span className="text-lg font-normal text-muted-foreground">{storeGroup.lineCount} approved lines</span>
                          </CardTitle>
                          <div className="mt-1 text-sm text-muted-foreground">
                            Store ID: {storeGroup.storeId} | Operator ID: {storeGroup.operatorId ?? 'Unassigned'}
                          </div>
                        </div>
                        <div className="text-right text-sm text-muted-foreground">
                          <div>Approved quantity</div>
                          <div className="text-2xl font-semibold text-foreground">{storeGroup.totalConfirmedQuantity}</div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Operator</TableHead>
                                <TableHead>Store</TableHead>
                                <TableHead>Posting Event</TableHead>
                                <TableHead>Campaign</TableHead>
                                <TableHead>Source</TableHead>
                                <TableHead>Media Format</TableHead>
                                <TableHead>Approved</TableHead>
                                <TableHead>Approver</TableHead>
                                <TableHead>Photo</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {campaignGroups.map((campaignGroup) => (
                                <Fragment key={campaignGroup.key}>
                                  {campaignGroup.rows.map((row, rowIndex) => (
                                    <TableRow key={row.id}>
                                      {rowIndex === 0 && (
                                        <>
                                          <TableCell rowSpan={campaignGroup.rows.length} className="align-top bg-slate-50/70 font-medium">
                                            {storeGroup.operatorName}
                                          </TableCell>
                                          <TableCell rowSpan={campaignGroup.rows.length} className="align-top">
                                            {storeGroup.storeName}
                                          </TableCell>
                                          <TableCell rowSpan={campaignGroup.rows.length} className="align-top">
                                            <div className="space-y-1">
                                              <div>Event #{campaignGroup.postingEventId}</div>
                                              {campaignGroup.reviewDecisionId && (
                                                <div className="text-xs text-muted-foreground">Review #{campaignGroup.reviewDecisionId}</div>
                                              )}
                                            </div>
                                          </TableCell>
                                          <TableCell rowSpan={campaignGroup.rows.length} className="align-top">
                                            {campaignGroup.campaignName}
                                          </TableCell>
                                          <TableCell rowSpan={campaignGroup.rows.length} className="align-top">
                                            <Badge variant={campaignGroup.sourceTaskType === 'REPOST' ? 'default' : 'secondary'}>
                                              {campaignGroup.sourceTaskType === 'REPOST' ? 'Repost' : 'Posting'}
                                            </Badge>
                                          </TableCell>
                                        </>
                                      )}

                                      <TableCell>{row.format.name}</TableCell>

                                      {rowIndex === 0 && (
                                        <>
                                          <TableCell rowSpan={campaignGroup.rows.length} className="align-top">
                                            <div className="space-y-1">
                                              <div className="font-medium">{campaignGroup.totalConfirmedQuantity}</div>
                                              <div className="text-xs text-muted-foreground">
                                                {campaignGroup.approvedAt ? new Date(campaignGroup.approvedAt).toLocaleString() : 'Unknown'}
                                              </div>
                                            </div>
                                          </TableCell>
                                          <TableCell rowSpan={campaignGroup.rows.length} className="align-top">
                                            {campaignGroup.approverName}
                                          </TableCell>
                                          <TableCell rowSpan={campaignGroup.rows.length} className="align-top">
                                            {campaignGroup.photoUrl ? (
                                              <img
                                                src={campaignGroup.photoUrl}
                                                alt={`${campaignGroup.campaignName} at ${storeGroup.storeName}`}
                                                className="h-12 w-12 rounded-md border object-cover"
                                              />
                                            ) : (
                                              <div className="flex h-12 w-12 items-center justify-center rounded-md border bg-muted text-muted-foreground">
                                                <ImageIcon className="h-4 w-4" />
                                              </div>
                                            )}
                                          </TableCell>
                                        </>
                                      )}
                                    </TableRow>
                                  ))}
                                </Fragment>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
